/**
 * 图片 URL 优化工具
 *
 * 支持腾讯云 COS 数据万象（CI）图片处理：
 *   ?imageMogr2/thumbnail/{width}x/quality/{quality}
 *
 * COS 图片走数据万象（CI）；腾讯 VOD 原始图片走 Next 图片优化缓存。
 */

import {
  isCosBucketUrl,
  toAbsoluteMediaUrl,
  toDisplayMediaUrl,
  toProxyMediaUrl,
  unwrapNextImageOptimizationUrl,
} from './media-url';
import { getAssetStreamUrl } from './api/media-proxy';

const WATERFALL_WIDTH_STEPS = [320, 480, 640, 800, 960, 1280] as const;
const WATERFALL_QUALITY = 75;

export interface ResponsiveImageSource {
  src: string;
  srcSet?: string;
  sizes?: string;
  widths: number[];
}

export interface GenerationSourceImage {
  originalImageUrl?: string | null;
  thumbnail?: string | null;
  assetId?: string | null;
  mediaType?: string | null;
  sourceLabel?: string | null;
}

export interface PrimaryMediaSource extends GenerationSourceImage {
  videoUrl?: string | null;
  modelUrl?: string | null;
}

const PUBLIC_SOURCE_LABELS = new Set(['explore', 'search', 'public assets']);
const VIDEO_URL_PATTERN = /\.(mp4|webm|mov|m3u8)(\?|$)/i;
const SVG_URL_PATTERN = /\.svg(\?|#|$)/i;
const TENCENT_VOD_IMAGE_HOST_RE = /\.vod2\.myqcloud\.com$/i;
const NEXT_IMAGE_WIDTHS = [256, 384, 640, 750, 828, 1080, 1200, 1920, 2048] as const;

function isTencentVodImageUrl(url: string): boolean {
  if (!url || isVideoUrl(url) || SVG_URL_PATTERN.test(url)) return false;
  try {
    return TENCENT_VOD_IMAGE_HOST_RE.test(new URL(url).hostname);
  } catch {
    return false;
  }
}

function toNextOptimizedImageUrl(url: string, width: number, quality: number): string {
  const requestedWidth = Math.max(1, Math.ceil(width));
  const optimizedWidth = NEXT_IMAGE_WIDTHS.find((candidate) => candidate >= requestedWidth)
    ?? NEXT_IMAGE_WIDTHS[NEXT_IMAGE_WIDTHS.length - 1];
  const optimizedQuality = Math.max(1, Math.min(100, Math.round(quality)));
  return `/_next/image?url=${encodeURIComponent(url)}&w=${optimizedWidth}&q=${optimizedQuality}`;
}

function isRelativeSiteMediaUrl(url?: string | null): boolean {
  return !!url && url.startsWith('/') && !url.startsWith('/api/');
}

export function getPreferredImageUrl(url?: string | null, thumbnailUrl?: string | null): string {
  return url || thumbnailUrl || '';
}

export function getGenerationSourceImageUrl(
  originalImageUrl?: string | null,
  thumbnailUrl?: string | null,
  assetId?: string | null,
  mediaType?: string | null,
): string {
  if (originalImageUrl) return toAbsoluteMediaUrl(originalImageUrl);
  if (assetId && (!mediaType || mediaType === 'image')) {
    return toAbsoluteMediaUrl(getAssetStreamUrl(assetId));
  }
  return toAbsoluteMediaUrl(thumbnailUrl);
}

export function getTaskResultImageUrl(resultUrl?: string | null, assetId?: string | null): string {
  // 已知直连地址优先，避免每张图先经过应用服务；持久化 asset stream
  // 由调用方保留为失效 URL 的最终兜底。
  const normalizedResultUrl = toDisplayMediaUrl(resultUrl);
  if (normalizedResultUrl) {
    return toAbsoluteMediaUrl(normalizedResultUrl);
  }

  if (assetId) {
    return toAbsoluteMediaUrl(getAssetStreamUrl(assetId));
  }

  return '';
}

export function getNodeGenerationSourceImageUrl(node?: GenerationSourceImage | null): string {
  if (!node) return '';

  if (node.originalImageUrl) {
    return toAbsoluteMediaUrl(node.originalImageUrl);
  }

  const normalizedThumbnail = toAbsoluteMediaUrl(node.thumbnail);
  const normalizedSourceLabel = node.sourceLabel?.trim().toLowerCase();
  if (normalizedThumbnail && (
    isRelativeSiteMediaUrl(node.thumbnail)
    || (normalizedSourceLabel ? PUBLIC_SOURCE_LABELS.has(normalizedSourceLabel) : false)
  )) {
    return normalizedThumbnail;
  }

  return getGenerationSourceImageUrl(node.originalImageUrl, node.thumbnail, node.assetId, node.mediaType);
}

export function getNodePreviewImageUrl(node?: GenerationSourceImage | null): string {
  if (!node) return '';

  if (node.thumbnail) {
    const thumbnailUrl = toAbsoluteMediaUrl(node.thumbnail);
    if (!VIDEO_URL_PATTERN.test(thumbnailUrl)) {
      return thumbnailUrl;
    }
  }

  if (node.originalImageUrl && node.mediaType !== 'video') {
    return toAbsoluteMediaUrl(node.originalImageUrl);
  }

  if (node.assetId && (!node.mediaType || node.mediaType === 'image')) {
    return toAbsoluteMediaUrl(getAssetStreamUrl(node.assetId));
  }

  return '';
}

export function getOptimizedImagePreviewUrl(
  url?: string | null,
  width: number = 160,
  quality: number = 68,
): string {
  const previewUrl = toAbsoluteMediaUrl(url);
  if (!previewUrl) {
    return '';
  }
  return resizeImageUrl(previewUrl, width, quality);
}

export function getOptimizedNodePreviewImageUrl(
  node?: GenerationSourceImage | null,
  width: number = 160,
  quality: number = 68,
): string {
  return getOptimizedImagePreviewUrl(getNodePreviewImageUrl(node), width, quality);
}

export function buildImageDisplaySourceCandidates(
  ...urls: Array<string | null | undefined>
): string[] {
  const candidates: string[] = [];
  const seen = new Set<string>();

  const add = (url?: string | null) => {
    if (!url || seen.has(url)) return;
    seen.add(url);
    candidates.push(url);
  };

  urls.forEach((url) => {
    const displayUrl = toDisplayMediaUrl(url);
    if (!displayUrl) return;
    // 展示优先直连 CDN。代理只保留为失败兜底；把公开图片先绕回应用
    // 服务会增加一次网络跳转，并可能被慢后端连接拖成白屏。
    add(displayUrl);
    if (isCosBucketUrl(displayUrl)) {
      add(toProxyMediaUrl(displayUrl));
    }
  });

  return candidates;
}

export function getNodePrimaryMediaUrl(node?: PrimaryMediaSource | null): string {
  if (!node) return '';

  if (node.mediaType === 'video') {
    return toAbsoluteMediaUrl(
      node.videoUrl || (node.assetId ? getAssetStreamUrl(node.assetId) : '') || node.thumbnail || ''
    );
  }

  if (node.mediaType === '3d_model') {
    return toAbsoluteMediaUrl(node.modelUrl || (node.assetId ? getAssetStreamUrl(node.assetId) : ''));
  }

  return getNodeGenerationSourceImageUrl(node);
}

function isCosImageUrl(url: string): boolean {
  return isCosBucketUrl(url) && !isVideoUrl(url) && !SVG_URL_PATTERN.test(url);
}

function hasCosTransform(url: string): boolean {
  if (!url) return false;

  const decodedUrl = decodeURIComponent(url);
  return decodedUrl.includes('?imageMogr2/')
    || decodedUrl.includes('&imageMogr2/')
    || decodedUrl.includes('?ci-process=')
    || decodedUrl.includes('&ci-process=');
}

function getSignedCosUrlParamList(url: string): Set<string> {
  try {
    const parsed = /^https?:\/\//i.test(url) ? new URL(url) : new URL(url, 'https://octolab.local');
    const rawList = parsed.searchParams.get('q-url-param-list') || '';
    return new Set(rawList.split(';').map((item) => item.trim().toLowerCase()).filter(Boolean));
  } catch {
    return new Set();
  }
}

function hasCosSignature(url: string): boolean {
  try {
    const parsed = /^https?:\/\//i.test(url) ? new URL(url) : new URL(url, 'https://octolab.local');
    return parsed.searchParams.has('q-signature') || parsed.searchParams.has('q-sign-algorithm');
  } catch {
    return decodeURIComponent(url).includes('q-signature=')
      || decodeURIComponent(url).includes('q-sign-algorithm=');
  }
}

function isCosTransformQueryPart(queryPart: string): boolean {
  const decodedPart = decodeURIComponent(queryPart).trim().toLowerCase();
  return decodedPart.startsWith('imagemogr2/')
    || decodedPart.startsWith('imagemogr2=')
    || decodedPart.startsWith('ci-process=');
}

function stripUnsignedCosTransformFromSignedUrl(url: string): string {
  if (!hasCosSignature(url) || !hasCosTransform(url)) return url;

  const signedParams = getSignedCosUrlParamList(url);
  if (signedParams.has('imagemogr2') || signedParams.has('ci-process')) {
    return url;
  }

  try {
    const isAbsolute = /^https?:\/\//i.test(url);
    const parsed = new URL(url, isAbsolute ? undefined : 'https://octolab.local');
    const queryParts = parsed.search ? parsed.search.slice(1).split('&').filter(Boolean) : [];
    const filteredParts = queryParts.filter((part) => !isCosTransformQueryPart(part));
    if (filteredParts.length === queryParts.length) return url;
    parsed.search = filteredParts.length > 0 ? `?${filteredParts.join('&')}` : '';
    return isAbsolute ? parsed.toString() : `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return url;
  }
}

function pickResponsiveWidth(targetWidth: number): number {
  const safeTarget = Math.max(WATERFALL_WIDTH_STEPS[0], Math.ceil(targetWidth));
  return WATERFALL_WIDTH_STEPS.find((width) => width >= safeTarget) ?? WATERFALL_WIDTH_STEPS[WATERFALL_WIDTH_STEPS.length - 1];
}

function toInlinePreviewImageUrl(url: string): string {
  const displayUrl = toDisplayMediaUrl(url);
  if (!displayUrl) return displayUrl;
  return displayUrl;
}

/**
 * 对 COS URL 应用图片缩放
 * @param url 原始图片 URL
 * @param width 目标宽度（px）
 * @param quality 压缩质量 0-100（默认 75）
 */
export function resizeImageUrl(url: string, width: number, quality: number = 75): string {
  if (!url) return url;
  const sourceUrl = unwrapNextImageOptimizationUrl(url);
  // VOD 图片通常是 3-8 MB 的原始 PNG。直接交给浏览器会让画布几十张图
  // 同时争抢带宽；Next 优化器首次转码后会持久缓存 AVIF/WebP。
  if (isTencentVodImageUrl(sourceUrl)) {
    return toNextOptimizedImageUrl(sourceUrl, width, quality);
  }
  // 仅对 COS URL 生效（含 .cos. 或 .myqcloud.com）
  if (!isCosImageUrl(sourceUrl)) return toDisplayMediaUrl(sourceUrl);
  if (hasCosSignature(sourceUrl)) return toInlinePreviewImageUrl(stripUnsignedCosTransformFromSignedUrl(sourceUrl));
  // 已存在 COS 图片处理参数时直接复用，避免重复拼接 imageMogr2/ci-process。
  if (hasCosTransform(sourceUrl)) return toInlinePreviewImageUrl(sourceUrl);
  // 不处理视频
  const separator = sourceUrl.includes('?') ? '&' : '?';
  // WebP 格式 + 渐进式加载 + 去除 EXIF → 比 JPEG 节省 25-35% 带宽
  return toInlinePreviewImageUrl(
    `${sourceUrl}${separator}imageMogr2/format/webp/thumbnail/${width}x/quality/${quality}/interlace/1/strip`
  );
}

/**
 * 为瀑布流卡片生成响应式图片资源。
 * 根据当前列宽输出离散档位的 src/srcSet，让浏览器按实际 DPR 选图。
 */
export function buildWaterfallImageSource(url: string, columnWidth: number): ResponsiveImageSource {
  if (!url) {
    return { src: url, widths: [] };
  }

  if (!isCosImageUrl(url)) {
    return { src: toDisplayMediaUrl(url), widths: [] };
  }

  const safeColumnWidth = Math.max(160, Math.ceil(columnWidth));
  const widths = Array.from(new Set(
    [safeColumnWidth, safeColumnWidth * 2, safeColumnWidth * 3].map(pickResponsiveWidth),
  ));

  return {
    src: toInlinePreviewImageUrl(resizeImageUrl(url, widths[0], WATERFALL_QUALITY)),
    srcSet: widths.map((width) => `${toInlinePreviewImageUrl(resizeImageUrl(url, width, WATERFALL_QUALITY))} ${width}w`).join(', '),
    sizes: `${safeColumnWidth}px`,
    widths,
  };
}

/**
 * 为瀑布流卡片生成适配宽度的缩略图 URL。
 * 保留单 URL 版本，供不需要 srcSet 的场景复用。
 */
export function waterfallThumbUrl(url: string, columnWidth: number = 400): string {
  return toInlinePreviewImageUrl(resizeImageUrl(url, pickResponsiveWidth(columnWidth), WATERFALL_QUALITY));
}

/**
 * 为画布 ClipNode 生成适配宽度的缩略图 URL
 * 画布节点支持放大查看，默认直接请求更高分辨率版本，优先保证清晰度。
 */
export function canvasThumbUrl(url: string): string {
  return resizeImageUrl(url, 2048, 92);
}

/** 画布缩放等级区间 — 控制缩略图分辨率 */
export type ZoomBracket = 'xs' | 'sm' | 'md' | 'lg';

/** 根据 zoom 值计算缩放区间 */
export function computeZoomBracket(zoom: number): ZoomBracket {
  if (zoom <= 0.3) return 'xs';
  if (zoom <= 0.55) return 'sm';
  if (zoom <= 0.8) return 'md';
  return 'lg';
}

const ZOOM_THUMB_CONFIG: Record<ZoomBracket, { width: number; quality: number }> = {
  xs: { width: 320, quality: 65 },
  sm: { width: 640, quality: 78 },
  md: { width: 1280, quality: 88 },
  lg: { width: 2048, quality: 92 },
};

const CANVAS_PREVIEW_WIDTH_STEPS = [160, 240, 320, 480, 640, 800, 960, 1280] as const;

function pickCanvasPreviewWidth(targetWidth: number): number {
  const safeTarget = Math.max(CANVAS_PREVIEW_WIDTH_STEPS[0], Math.ceil(targetWidth));
  return CANVAS_PREVIEW_WIDTH_STEPS.find((width) => width >= safeTarget)
    ?? CANVAS_PREVIEW_WIDTH_STEPS[CANVAS_PREVIEW_WIDTH_STEPS.length - 1];
}

export interface CanvasPreviewThumbOptions {
  displayWidth: number;
  zoom?: number;
  devicePixelRatio?: number;
  minWidth?: number;
  maxWidth?: number;
  overscanFactor?: number;
}

/**
 * 根据画布缩放等级动态选择缩略图分辨率。
 * 当前策略明显偏向清晰度，放大节点时不再把图片压到 640px。
 */
export function canvasThumbUrlForZoom(url: string, bracket: ZoomBracket): string {
  const config = ZOOM_THUMB_CONFIG[bracket];
  return resizeImageUrl(url, config.width, config.quality);
}

/**
 * 为画布节点按实际屏幕尺寸选择缩略图，避免低缩放时仍请求 1280/2048 大图。
 */
export function canvasThumbUrlForDisplay(
  url: string,
  options: CanvasPreviewThumbOptions,
): string {
  if (!url) return url;

  const zoom = Number.isFinite(options.zoom) ? Math.max(options.zoom ?? 1, 0.1) : 1;
  const devicePixelRatio = Number.isFinite(options.devicePixelRatio)
    ? Math.min(Math.max(options.devicePixelRatio ?? 1, 1), 2)
    : 1;
  const overscanFactor = Number.isFinite(options.overscanFactor)
    ? Math.max(options.overscanFactor ?? 1.1, 1)
    : 1.1;
  const minWidth = options.minWidth ?? CANVAS_PREVIEW_WIDTH_STEPS[0];
  const maxWidth = options.maxWidth ?? CANVAS_PREVIEW_WIDTH_STEPS[CANVAS_PREVIEW_WIDTH_STEPS.length - 1];

  const desiredWidth = Math.max(options.displayWidth * zoom * devicePixelRatio * overscanFactor, minWidth);
  const boundedWidth = Math.min(desiredWidth, maxWidth);
  const width = pickCanvasPreviewWidth(boundedWidth);
  const quality = width >= 960 ? 84 : width >= 640 ? 80 : width >= 320 ? 74 : 70;

  return resizeImageUrl(url, width, quality);
}

/**
 * 为序列区域的小缩略图生成 URL
 * 64px 卡片，2x DPR → 128px
 */
export function sequenceThumbUrl(url: string): string {
  return resizeImageUrl(url, 128, 60);
}

/**
 * 视频截图曾依赖 COS ci-process=snapshot；当前桶未启用媒体处理，
 * 继续拼接会产生 400 XML 并被代理报 502。保留函数名兼容调用方，
 * 但不再生成派生截图 URL。
 */
export function videoSnapshotUrl(url: string, width: number = 128): string {
  void width;
  return url;
}

/**
 * 判断 URL 是否为视频格式
 */
export function isVideoUrl(url: string): boolean {
  return /\.(mp4|webm|mov|m3u8)(\?|$)/i.test(url);
}
